import { useState, useEffect, Suspense } from 'react';
import { useTenant } from '../../contexts/TenantContext';
import ProfessionalLoader from '../../components/common/ProfessionalLoader';
import { getTheme } from '../../components/themes';

const Homepage = () => {
  const { tenant, loading, error } = useTenant();
  const [homepageContent, setHomepageContent] = useState(null);
  const [contentLoading, setContentLoading] = useState(true);

  useEffect(() => {
    if (tenant) {
      fetchHomepageContent();
    }
  }, [tenant]);

  const fetchHomepageContent = async () => {
    try {
      // Check if custom homepage page exists via the pages endpoint
      const response = await fetch(`/api/public/pages/homepage`, {
        headers: {
          'Accept': 'application/json',
          'X-Original-Host': window.location.hostname
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data?.page) {
          setHomepageContent(data.data.page);
        }
      }
    } catch (error) {
      // Silently fall back to theme homepage - this is expected behavior
      console.debug('Using theme homepage');
    } finally {
      setContentLoading(false);
    }
  };

  // Don't show loading here - TenantProvider already handles it
  // This prevents double loading/blinking
  if (loading) {
    return null;
  }

  if (error) {
    return (
      <div className="tenant-error">
        <div className="error-container">
          <i className="fas fa-exclamation-triangle"></i>
          <h2>Tenant Not Found</h2>
          <p>The requested organization could not be found.</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  // If custom homepage content exists, show it
  if (!contentLoading && homepageContent) {
    return (
      <div className="custom-homepage" dangerouslySetInnerHTML={{__html: homepageContent.content}} />
    );
  }

  // Get the theme for this tenant (defaults to throwball)
  const themeName = tenant?.theme || 'throwball';
  const theme = getTheme(themeName);

  // Use theme's layout and homepage
  if (theme.Layout && theme.Homepage) {
    const ThemeLayout = theme.Layout;
    const ThemeHomepage = theme.Homepage;
    return (
      <Suspense fallback={<ProfessionalLoader />}>
        <ThemeLayout>
          <ThemeHomepage />
        </ThemeLayout>
      </Suspense>
    );
  }

  // Fallback loading state
  return <ProfessionalLoader />;
};

export default Homepage;
